import React, { useEffect, useState, useCallback } from 'react';
import { Command } from 'cmdk';
import { Search, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useGlobalSearch, type SearchResult } from '@/hooks/useGlobalSearch';
import { SearchResults } from './SearchResults';

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

export function CommandPalette({ open, onClose }: CommandPaletteProps) {
  const navigate = useNavigate();
  const { query, setQuery, results, isSearching, addToRecentSearches } = useGlobalSearch();
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Calculate total results count
  const totalResults = results.reduce((acc, group) => acc + group.results.length, 0);

  // Reset selected index when results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [results]);

  // Reset query when closing
  useEffect(() => {
    if (!open) {
      setQuery('');
      setSelectedIndex(0);
    }
  }, [open, setQuery]);

  // Handle result selection
  const handleSelect = useCallback(
    (result: SearchResult) => {
      // Add to recent searches if it was a search query
      if (query.trim()) {
        addToRecentSearches(query);
      }

      // Execute action or navigate
      if (result.action) {
        result.action();
      } else if (result.url) {
        navigate(result.url);
      }

      // Close palette
      onClose();
    },
    [query, addToRecentSearches, navigate, onClose]
  );

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) => (prev + 1) % totalResults);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => (prev - 1 + totalResults) % totalResults);
          break;
        case 'Enter':
          e.preventDefault();
          if (totalResults > 0) {
            let currentIndex = 0;
            for (const group of results) {
              for (const result of group.results) {
                if (currentIndex === selectedIndex) {
                  handleSelect(result);
                  return;
                }
                currentIndex++;
              }
            }
          }
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
      }
    },
    [selectedIndex, totalResults, results, onClose, handleSelect]
  );

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 animate-in fade-in-0 duration-200"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Command Palette */}
      <div
        className="fixed inset-0 z-50 flex items-start justify-center pt-[20vh] px-4"
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
      >
        <Command
          className="relative w-full max-w-2xl bg-background rounded-lg border border-border shadow-2xl animate-in fade-in-0 zoom-in-95 duration-200"
          onKeyDown={handleKeyDown}
          shouldFilter={false}
          loop={false}
        >
          {/* Search Input */}
          <div className="flex items-center border-b border-border px-4">
            <Search className="h-5 w-5 text-muted-foreground flex-shrink-0" />
            <Command.Input
              value={query}
              onValueChange={setQuery}
              placeholder="Search entities, documents, pages, or actions..."
              className="flex-1 px-3 py-4 text-sm outline-none bg-transparent placeholder:text-muted-foreground"
              autoFocus
            />
            {isSearching && (
              <div className="flex-shrink-0 animate-spin rounded-full h-4 w-4 border-2 border-primary border-t-transparent" />
            )}
            {query && (
              <button
                onClick={() => setQuery('')}
                className="flex-shrink-0 p-1 rounded-sm hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Clear search"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Results */}
          <Command.List className="max-h-[400px] overflow-y-auto overflow-x-hidden">
            <SearchResults
              groups={results}
              selectedIndex={selectedIndex}
              onSelect={handleSelect}
              query={query}
            />
          </Command.List>

          {/* Footer */}
          <div className="flex items-center justify-between px-4 py-2.5 border-t border-border bg-muted/30">
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <div className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 font-mono bg-background border border-border rounded">
                  &uarr;
                </kbd>
                <kbd className="px-1.5 py-0.5 font-mono bg-background border border-border rounded">
                  &darr;
                </kbd>
                <span>Navigate</span>
              </div>
              <div className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 font-mono bg-background border border-border rounded">
                  Enter
                </kbd>
                <span>Select</span>
              </div>
              <div className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 font-mono bg-background border border-border rounded">
                  Esc
                </kbd>
                <span>Close</span>
              </div>
            </div>
            {totalResults > 0 && (
              <div className="text-xs text-muted-foreground">
                {totalResults} {totalResults === 1 ? 'result' : 'results'}
              </div>
            )}
          </div>
        </Command>
      </div>
    </>
  );
}

export default CommandPalette;
